import { useTranslation } from 'react-i18next';
import { Card } from '../common/Card';
import { Badge } from '../common/Badge';
import { formatPercent } from '../../utils/formatters';
import { Robot } from '@phosphor-icons/react';

export function AIAnalysis({ prediction, market, accessStatus }) {
  const { t } = useTranslation();
  const isAuthRequired = accessStatus?.isAuthRequired;
  const isMembershipRequired = accessStatus?.isMembershipRequired;

  if (!prediction) {
    const emptyState = isAuthRequired
      ? { text: t('auth.loginRequired', '请先登录'), className: 'text-accent' }
      : isMembershipRequired
      ? { text: t('membership.noAccess', '没有会员权限'), className: 'text-accent-warm' }
      : { text: t('detail.noAIData'), className: 'text-gray-500' };
    return (
      <Card>
        <h3 className="font-semibold text-accent mb-4 flex items-center gap-2">
          <Robot size={20} weight="duotone" /> {t('detail.aiAnalysis')}
        </h3>
        <div className={`${emptyState.className} text-sm text-center py-8`}>
          {emptyState.text}
        </div>
      </Card>
    );
  }

  const { 
    prob, 
    confidence, 
    aiConfidence,
    keyFactors = [], 
    reasoning = [],
    marketComparison,
    diffReason,
    side,
    edge
  } = prediction;

  // Calculate win probabilities
  const teamAProb = prob || 0.5;
  const teamBProb = 1 - teamAProb;

  // Confidence badge color
  const getConfidenceBadge = () => {
    const conf = aiConfidence || (confidence > 0.8 ? 'high' : confidence > 0.6 ? 'medium' : 'low');
    const colors = {
      high: 'positive',
      medium: 'neutral',
      low: 'negative'
    };
    return { text: conf.toUpperCase(), variant: colors[conf] || 'neutral' };
  };

  const confBadge = getConfidenceBadge();

  return (
    <Card>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-accent flex items-center gap-2">
          <Robot size={20} weight="duotone" /> {t('detail.aiAnalysis')}
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">XGBoost-V5 + DeepSeek Hybrid</span>
        </div>
      </div>

      {/* Confidence Badge */}
      <div className="flex items-center gap-3 mb-4">
        <span className="text-sm text-gray-400">{t('detail.confidence')}</span>
        <Badge variant={confBadge.variant}>{confBadge.text}</Badge>
        <span className="text-2xl font-bold">{Math.round((confidence || 0.85) * 100)}%</span>
      </div>

      {/* Win Probability Bars */}
      <div className="mb-6 space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm">{market?.teamA} {t('detail.winRate')}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-4 bg-surface-highlight/60 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-accent-strong to-accent rounded-full transition-all"
                style={{ width: `${teamAProb * 100}%` }}
              />
            </div>
            <span className="text-lg font-bold w-16 text-right">{(teamAProb * 100).toFixed(1)}%</span>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm">{market?.teamB} {t('detail.winRate')}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-4 bg-surface-highlight/60 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-accent to-accent-warm rounded-full transition-all"
                style={{ width: `${teamBProb * 100}%` }}
              />
            </div>
            <span className="text-lg font-bold w-16 text-right">{(teamBProb * 100).toFixed(1)}%</span>
          </div>
        </div>
      </div>

      {/* Key Factors */}
      {keyFactors.length > 0 && (
        <div className="mb-4">
          <div className="space-y-2">
            {keyFactors.map((factor, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <span className="text-accent">📌</span>
                <span className="text-gray-300">{factor}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reasoning */}
      {reasoning.length > 0 && (
        <div className="mb-4 p-4 bg-surface-highlight/50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <span>💡</span>
            <span className="text-sm font-medium">{t('detail.analysisReasoning')}</span>
          </div>
          <p className="text-sm text-gray-300 leading-relaxed">
            {Array.isArray(reasoning) ? reasoning.join(' ') : reasoning}
          </p>
        </div>
      )}

      {/* Market Comparison Warning */}
      {marketComparison && (
        <div className="mb-4 p-4 bg-accent/10 border border-accent/30 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <span>📊</span>
            <span className="text-sm font-medium text-accent">{t('detail.marketComparison')}</span>
          </div>
          <p className="text-sm text-gray-300">{marketComparison}</p>
        </div>
      )}

      {/* Model Difference Warning */}
      {diffReason && (
        <div className="p-4 bg-accent-warm/10 border border-accent-warm/30 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <span>⚠️</span>
            <span className="text-sm font-medium text-accent-warm">{t('detail.modelDifference')}</span>
          </div>
          <p className="text-sm text-gray-300">{diffReason}</p>
        </div>
      )}
    </Card>
  );
}
